<script setup lang="ts">
import { reactive } from 'vue';
import { ElMessage } from 'element-plus';
import { useAdminStore } from '../stores/admin';

const store = useAdminStore();

const categoryDraft = reactive({ id: "", name: "", sortOrder: "0" });

async function saveStore() {
  if (!store.storeProfile.name.trim() || !store.storeProfile.address.trim()) {
    return ElMessage.warning("门店名称和完整地址必填");
  }
  store.storeSaving = true;
  try {
    await store.adminFetch("/api/admin/store", {
      method: "POST",
      body: JSON.stringify(store.storeProfile)
    });
    ElMessage.success("门店资料保存成功！");
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : "门店资料保存失败");
  } finally {
    store.storeSaving = false;
  }
}

async function createCategory() {
  if (!categoryDraft.id.trim() || !categoryDraft.name.trim()) return ElMessage.warning("分类 ID 和名称必填");
  try {
    await store.adminFetch("/api/admin/categories", {
      method: "POST",
      body: JSON.stringify({ id: categoryDraft.id.trim(), name: categoryDraft.name.trim(), sortOrder: Number(categoryDraft.sortOrder) || 0, active: true })
    });
    Object.assign(categoryDraft, { id: "", name: "", sortOrder: "0" });
    await store.loadCategories();
    ElMessage.success("分类添加成功");
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : "分类添加失败");
  }
}

async function updateCategory(category: { id: string; name: string; sortOrder?: number }) {
  try {
    await store.adminFetch(`/api/admin/categories/${encodeURIComponent(category.id)}`, {
      method: "POST",
      body: JSON.stringify({ id: category.id, name: category.name, sortOrder: Number(category.sortOrder ?? 0), active: true })
    });
    await store.loadCategories();
    ElMessage.success("分类保存成功");
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : "分类保存失败");
  }
}

async function removeCategory(category: { id: string }) {
  try {
    await store.adminFetch(`/api/admin/categories/${encodeURIComponent(category.id)}`, { method: "DELETE" });
    await store.loadCategories();
    ElMessage.success("分类已下架");
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : "分类下架失败");
  }
}

async function refreshNotificationSettings() {
  await Promise.all([store.loadNotificationStatus(), store.loadNotificationRecipients()]);
}

async function createNotificationBinding() {
  try {
    await store.createNotificationBinding();
    ElMessage.success("绑定码及邀请路径已生成，长期有效（支持多名店员复用绑定）");
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : "绑定码生成失败");
  }
}

function copyInvitePath() {
  if (!store.notificationBinding) return;
  const path = store.notificationBinding.sharePath || `/pages/merchant-notification-bind/index?ticket=${store.notificationBinding.ticket}`;
  navigator.clipboard.writeText(path).then(() => {
    ElMessage.success("小程序邀请路径已复制");
  }).catch(() => {
    ElMessage.info(`请手动复制：${path}`);
  });
}

async function disableNotificationRecipient(id: string) {
  try {
    await store.disableNotificationRecipient(id);
    ElMessage.success("该微信接收账号已停用");
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : "停用失败");
  }
}
</script>

<template>
  <div class="settings-container">
    <!-- Store Profile Settings -->
    <div class="panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">店铺配置</p>
          <h2>门店资料设置</h2>
        </div>
      </div>

      <el-form label-position="top" class="settings-form">
        <el-form-item label="门店名称" required>
          <el-input v-model="store.storeProfile.name" placeholder="例如 码头海鲜超市" />
        </el-form-item>
        <el-form-item label="完整地址 (供顾客自提导航)" required>
          <el-input v-model="store.storeProfile.address" placeholder="省 / 市 / 区 / 街道门牌号" />
        </el-form-item>
        <el-form-item label="联系电话">
          <el-input v-model="store.storeProfile.contactPhone" placeholder="门店联系电话" />
        </el-form-item>
        <el-form-item label="营业时间">
          <el-input v-model="store.storeProfile.businessHours" placeholder="例如 每日 09:00-20:00" />
        </el-form-item>
        <el-form-item label="自提说明与到店提示">
          <el-input v-model="store.storeProfile.pickupInstructions" type="textarea" rows="2" placeholder="到店提示和核销说明" />
        </el-form-item>
        
        <el-button type="primary" size="large" :loading="store.storeSaving" @click="saveStore">
          保存门店资料
        </el-button>
      </el-form>
    </div>

    <div class="panel notification-panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">商家提醒</p>
          <h2>微信新订单提醒</h2>
        </div>
        <el-button text type="primary" @click="refreshNotificationSettings">刷新状态</el-button>
      </div>
      <p class="notification-status-copy">{{ store.notificationStatus.message }}</p>
      <p class="notification-hint">微信提醒只发送给完成绑定并主动同意授权的商家账号，不会发送给普通顾客。请先在微信后台确认模板关键词，并将对应字段映射配置到生产环境；是否能持续接收仍取决于微信模板和账号权限，若显示订阅失效需重新绑定。</p>
      <div class="notification-actions">
        <el-button type="primary" @click="createNotificationBinding">生成绑定码</el-button>
        <el-button plain @click="store.enableDesktopNotifications">启用后台桌面提醒</el-button>
      </div>
      <div v-if="store.notificationBinding" class="binding-result">
        <div><strong>绑定码：</strong><code>{{ store.notificationBinding.ticket }}</code></div>
        <div style="margin-top: 6px;">
          <strong>小程序邀请路径：</strong>
          <code>{{ store.notificationBinding.sharePath || ('/pages/merchant-notification-bind/index?ticket=' + store.notificationBinding.ticket) }}</code>
          <el-button size="small" style="margin-left: 8px;" @click="copyInvitePath">复制路径</el-button>
        </div>
        <div class="binding-expiry" style="margin-top: 6px;">长期有效（支持多名店员复用绑定）</div>
        <div class="binding-hint">店员可直接打开该路径一键绑定，支持二次转发与多人使用；也可由店长或已绑定店员在小程序中直接发微信卡片给店员。</div>
      </div>
      <div v-if="store.notificationRecipients.length" class="recipient-list">
        <div v-for="recipient in store.notificationRecipients" :key="recipient.id" class="recipient-row">
          <div>
            <strong>{{ recipient.label || '商家微信账号' }}</strong>
            <el-tag
              size="small"
              :type="(recipient.remainingCount ?? 0) > 10 ? 'success' : (recipient.remainingCount ?? 0) > 0 ? 'warning' : 'danger'"
              style="margin-left: 8px;"
            >
              {{ (recipient.remainingCount ?? 0) > 0 ? `剩余 ${recipient.remainingCount} 笔` : '额度耗尽（待充能）' }}
            </el-tag>
            <small>绑定于 {{ recipient.boundAt ? new Date(recipient.boundAt).toLocaleString() : '未知时间' }}</small>
            <small v-if="recipient.lastErrorCode" class="recipient-error">最近失败：{{ recipient.lastErrorCode }}</small>
          </div>
          <el-button v-if="recipient.active" type="danger" text @click="disableNotificationRecipient(recipient.id)">停用</el-button>
          <el-tag v-else type="info">已停用</el-tag>
        </div>
      </div>
      <p v-else class="notification-empty">暂无已绑定的商家微信账号。</p>
    </div>

    <!-- Category Management -->
    <div class="panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">海货分类</p>
          <h2>商品分类管理 (共 {{ store.categories.length }} 个)</h2>
        </div>
      </div>

      <div class="category-add-row">
        <el-input v-model="categoryDraft.id" placeholder="分类 ID (英文)" style="width: 130px" />
        <el-input v-model="categoryDraft.name" placeholder="分类名称 (中文)" style="flex: 1" />
        <el-input v-model="categoryDraft.sortOrder" placeholder="排序号" style="width: 90px" />
        <el-button type="primary" @click="createCategory">新增分类</el-button>
      </div>

      <div class="category-list">
        <div v-for="cat in store.categories" :key="cat.id" class="category-item">
          <span class="cat-id-badge">{{ cat.id }}</span>
          <el-input v-model="cat.name" style="flex: 1" />
          <el-input v-model="cat.sortOrder" style="width: 80px" />
          <el-button type="success" text @click="updateCategory(cat)">保存</el-button>
          <el-button type="danger" text @click="removeCategory(cat)">下架</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.settings-container {
  display: grid;
  gap: 20px;
  max-width: 800px;
}
.settings-form {
  max-width: 600px;
}
.notification-panel {
  max-width: 800px;
}
.notification-status-copy {
  margin: 0;
  color: #315a85;
  font-weight: 600;
}
.notification-hint,
.notification-empty,
.binding-hint,
.binding-expiry {
  color: #71839a;
  font-size: 13px;
  line-height: 1.6;
}
.notification-actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  flex-wrap: wrap;
}
.binding-result {
  display: grid;
  gap: 6px;
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid #dce8f5;
  border-radius: 8px;
  background: #f7fbff;
  word-break: break-all;
}
.binding-result code {
  color: #0b1d3a;
  font-size: 20px;
  letter-spacing: 3px;
}
.recipient-list {
  display: grid;
  gap: 8px;
  margin-top: 18px;
}
.recipient-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f8fafc;
}
.recipient-row > div {
  display: grid;
  gap: 4px;
}
.recipient-row small {
  color: #8494a8;
  font-size: 12px;
}
.recipient-error {
  color: #c45656 !important;
}
.category-add-row {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.category-list {
  display: grid;
  gap: 10px;
}
.category-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #f8fafc;
  border-radius: 6px;
}
.cat-id-badge {
  font-size: 12px;
  color: #909399;
  font-weight: 600;
  width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 600px) {
  .category-add-row {
    flex-direction: column;
  }
  .category-add-row .el-input {
    width: 100% !important;
  }
  .category-item {
    flex-wrap: wrap;
  }
}
</style>
